import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ReplyTypeTable from '../../../components/tables/ReplyTypeTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useReplyTypeApi } from '../../../hooks/useReplyTypeApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { ReplyTypeDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const ReplyTypeList: React.FC = () => {
  const { search, remove, isLoading, error } = useReplyTypeApi();
  const [items, setItems] = useState<ReplyTypeDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Reply Types', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Reply Types:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: ReplyTypeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting ReplyType:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/replytypes"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission='Settings.ReplyType.View'
    >
    <Card
        title="Manage Reply Types"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            onClick={() => navigate('/admin/replytypes/create')}
          />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                    <FormField
                            id="title"
                            label="Search by Title"
                            value={searchFilters.title || ''}
                            onChange={(e) => setSearchFilters({ ...searchFilters, title: e.target.value })}
                            placeholder="Search by Title"
                        />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <ReplyTypeTable
          data={items}
          handleEdit={(dto) =>
            navigate(`/admin/replytypes/${dto.id}`)
          }
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default ReplyTypeList;